
import React, { useState, useEffect } from 'react';
import { LdsModal, LdsButton } from "@elilillyco/ux-lds-react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import activityTracker from '../utils/activityTracker';
import "../assets/scss/ServiceNowGuidanceModal.scss";

const ServiceNowGuidanceModal = ({ isModalOpen, setIsModalOpen }) => {
  const { name } = useSelector(({ user }) => user);
  const role = sessionStorage.getItem("role");
  const location = useLocation();
  const [copiedField, setCopiedField] = useState(null);

  // Track navigation when location changes
  useEffect(() => {
    if (location.pathname) {
      activityTracker.trackNavigation(location.pathname);
    }
  }, [location]);

  // Determine current context
  const currentPage = location.pathname.substring(1) || 'Home';
  const environment = window.location.hostname.includes('localhost')
    ? 'Local Development' 
    : window.location.hostname.includes('dev') 
      ? 'Development' 
      : window.location.hostname.includes('qa') 
        ? 'QA' 
        : 'Production';

  // Pre-determined values for incident form
  const incidentGuidance = {
    assignmentGroup: 'TAXONOMYOPS-SUPPORT',
    configurationItem: 'CI00000022166404',
    configurationItemDisplay: 'Specialty Hub Tool',
    category: 'Business Service',
    subcategory: 'Services',
    area: 'Error Message',
    shortDescription: 'Specialty Hub Tool - Issue Report',
    description: 'Issue reported from Specialty Hub Tool application',
    contactType: 'Web',
    gssFormUrl: 'https://lilly.service-now.com/now/nav/ui/classic/params/target/com.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1%26sysparm_id%3De8126642db685f006b3f7d71ba9619ca'
  };

  // Priority guidance with Specialty Hub Tool-specific examples
  const priorityGuidance = [
    {
      level: 'High Impact + High Urgency',
      impact: 'High',
      impactDescription: 'A critical business process is affected (work can\'t continue normally). Impacts multiple users in multiple locations.',
      urgency: 'High',
      urgencyDescription: 'No workaround available; business operations cannot continue normally',
      examples: [
        'Application completely down - unable to access Specialty Hub Tool',
        'Cannot create or approve specialty lists - blocking critical workflow',
        'MLR approval process completely broken - affecting multiple brands',
        'Database connection failure - all specialty data inaccessible'
      ]
    },
    {
      level: 'High Impact + Medium Urgency',
      impact: 'High',
      impactDescription: 'A critical business process is affected. Impacts multiple users in multiple locations.',
      urgency: 'Medium',
      urgencyDescription: 'Workaround is available; business operations can continue with limitations',
      examples: [
        'Specialty list creation slow but functional with manual refresh',
        'Reports not generating automatically but can export data manually',
        'Some brands affected but others working normally',
        'Performance degradation affecting productivity but work continues'
      ]
    },
    {
      level: 'Medium Impact + Medium Urgency',
      impact: 'Medium',
      impactDescription: 'A regular business process isn\'t working properly, but overall operations can continue. Impacts multiple users in a single location.',
      urgency: 'Medium',
      urgencyDescription: 'Workaround is available; business operations can continue with limitations',
      examples: [
        'Search functionality not working but can browse lists manually',
        'Email notifications delayed but can check status in application',
        'Export feature intermittent but data accessible in the tool',
        'Non-critical feature malfunction with manual alternative'
      ]
    },
    {
      level: 'Low Impact + Low Urgency',
      impact: 'Low',
      impactDescription: 'A process isn\'t working as expected, but users don\'t notice any problems. Impacts a single user in a single location.',
      urgency: 'Low',
      urgencyDescription: 'No disruption to normal business operations',
      examples: [
        'UI display issues or formatting problems',
        'Typos in labels or help text',
        'Minor cosmetic issues that don\'t affect functionality',
        'Enhancement requests or feature suggestions'
      ]
    }
  ];

  const copyToClipboard = (text, fieldName) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // Generate comprehensive incident details (includes user context automatically)
  const generateIncidentDetails = () => {
    const activity = activityTracker.getFormattedActivity();
    
    return `--- ISSUE DESCRIPTION ---
[Please describe your issue here in detail]

--- STEPS TO REPRODUCE ---
1. [Step 1]
2. [Step 2]
3. [Step 3]

--- EXPECTED BEHAVIOR ---
[What you expected to happen]

--- ACTUAL BEHAVIOR ---
[What actually happened]

--- ADDITIONAL NOTES ---
[Any other relevant information]

--- USER CONTEXT ---
User: ${name || 'Not logged in'}
Role: ${role || 'N/A'}
Current Page: ${currentPage}
Environment: ${environment}

--- RECENT ACTIVITY (Last 10) ---
Recent Pages Visited:
${activity.navigation}

Recent API Calls:
${activity.apiCalls}
`;
  };

  // Copy all incident details
  const copyAllDetails = () => {
    const allDetails = generateIncidentDetails();
    navigator.clipboard.writeText(allDetails).then(() => {
      setCopiedField('allDetails');
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const handleProceedToServiceNow = () => {
    window.open(incidentGuidance.gssFormUrl, '_blank');
  };

  const FieldRow = ({ label, value, fieldName }) => (
    <div className="field-row">
      <div className="field-label">
        <strong>{label}:</strong>
      </div>
      <div className="field-value">
        <span className="value-text">{value}</span>
        <LdsButton
          classes="outlined small"
          onClick={() => copyToClipboard(value, fieldName)}
          aria-label={`Copy ${label}`}
        >
          {copiedField === fieldName ? '✓ Copied!' : '📋 Copy'}
        </LdsButton>
      </div>
    </div>
  );

  return (
    <LdsModal
      className="servicenow-guidance-modal"
      modalId="servicenow-guidance-modal"
      open={isModalOpen}
      setModalOpen={setIsModalOpen}
      size="large"
    >
      <div className="modal-header">
        <h2>🎫 Report ServiceNow Incident</h2>
        <p className="subtitle">4-step guide to help you fill out and submit the GSS incident form (user context auto-included)</p>
      </div>

      <div className="modal-content">
        {/* Step 1: User Context - COMMENTED OUT - Now auto-included in Step 3 (Complete Incident Report) */}
        {/* User context (name, role, page, environment) is automatically included in the complete incident report */}
        {/* 
        <div className="section context-section">
          <div className="section-header-with-button">
            <h3>Step 1: Copy Your Current Context for the Form</h3>
            <LdsButton
              classes="outlined small"
              onClick={copyContextToClipboard}
              aria-label="Copy all context information"
            >
              {copiedField === 'context' ? '✓ Copied!' : '📋 Copy'}
            </LdsButton>
          </div>
          <p className="step-instruction">
            Copy this information to include in your incident description. This helps the support team understand your situation.
          </p>
          <div className="context-grid">
            <div className="context-item">
              <span className="context-label">User:</span>
              <span className="context-value">{name || 'Not logged in'}</span>
            </div>
            <div className="context-item">
              <span className="context-label">Role:</span>
              <span className="context-value">{role || 'N/A'}</span>
            </div>
            <div className="context-item">
              <span className="context-label">Current Page:</span>
              <span className="context-value">{currentPage}</span>
            </div>
            <div className="context-item">
              <span className="context-label">Environment:</span>
              <span className="context-value">{environment}</span>
            </div>
          </div>
        </div>
        */}

        {/* Step 1: Application Information */}
        <div className="section app-info-section">
          <h3>Step 1: Copy Application Details to Fill in GSS Form</h3>
          <p className="step-instruction">
            Use these exact values when filling out the GSS incident form. Click the copy button next to each field to copy the value.
          </p>
          
          <div className="fields-container">
            <FieldRow 
              label="Application Name" 
              value={incidentGuidance.configurationItemDisplay} 
              fieldName="appName"
            />
            <FieldRow 
              label="Configuration Item (CI)" 
              value={incidentGuidance.configurationItem} 
              fieldName="configurationItem"
            />
            <FieldRow 
              label="Assignment Group" 
              value={incidentGuidance.assignmentGroup} 
              fieldName="assignmentGroup"
            />
          </div>
        </div>

        {/* Step 2: Impact & Urgency Selection */}
        <div className="section required-fields-section">
          <h3>Step 2: Select Impact & Urgency Levels</h3>
          <p className="step-instruction">
            Choose the appropriate combination based on your situation:<br/>
            <strong>Impact</strong> = how many users affected<br/>
            <strong>Urgency</strong> = can work continue?
          </p>
          
          <div className="priority-cards">
            {priorityGuidance.map((priority, index) => (
              <div key={index} className={`priority-card priority-level-${index + 1} compact`}>
                <div className="priority-header">
                  <strong>{priority.level}</strong>
                </div>
                <div className="priority-meta">
                  <span className="priority-badge impact">Impact: {priority.impact}</span>
                  <span className="priority-badge urgency">Urgency: {priority.urgency}</span>
                </div>
                <div className="priority-examples compact">
                  <strong>Examples:</strong>
                  <ul>
                    {priority.examples.slice(0, 2).map((example, idx) => (
                      <li key={idx}>{example}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Activity tracking and user context are automatically included in the complete incident report below */}
        {/* No separate step needed - all context is captured when copying complete incident report */}

        {/* Step 3: Copy All Details (includes user context automatically) */}
        <div className="section copy-details-section">
          <div className="section-header-with-button inline">
            <h3>Step 3: Copy Complete Incident Report (Includes All Context Automatically)</h3>
            <LdsButton
              classes="primary"
              onClick={copyAllDetails}
              aria-label="Copy all incident details"
            >
              {copiedField === 'allDetails' ? '✓ Copied!' : '📋 Copy All Incident Details'}
            </LdsButton>
          </div>
          <p className="step-instruction">
            Click the button to copy a complete incident report that automatically includes: your user context (name, role, current page, environment), recent activity, and a template for describing your issue.
          </p>
        </div>

        {/* Step 4: Submit Incident */}
        <div className="section instructions-section">
          <h3>Step 4: Open GSS Form and Submit Your Incident</h3>
          <p className="step-instruction">
            Click the button below to open the ServiceNow GSS incident form, then follow these steps:
          </p>
          <ol className="instructions-list">
            <li>
              <strong>Select Impact level</strong> - Choose from High, Medium, or Low based on Step 2 guidance above
            </li>
            <li>
              <strong>Select Urgency level</strong> - Choose from High, Medium, or Low based on Step 2 guidance above
            </li>
            <li>
              <strong>Verify Configuration Item</strong> - Should show: <code>{incidentGuidance.configurationItem}</code>
            </li>
            <li>
              <strong>Fill in Short Description</strong> - Brief summary of the issue (e.g., "Unable to create specialty list")
            </li>
            <li>
              <strong>Add Detailed Description</strong> - Paste the copied information from Step 3, then add:
              <ul>
                <li>What you were trying to do</li>
                <li>What went wrong</li>
                <li>Any error messages you saw</li>
                <li>Screenshots if applicable</li>
              </ul>
            </li>
            <li>
              <strong>Submit the form</strong> - Review all information and click Submit. Note the incident number for your records.
            </li>
          </ol>
        </div>
      </div>

      <div className="modal-footer">
        <LdsButton
          classes="outlined"
          onClick={() => setIsModalOpen(false)}
        >
          Cancel
        </LdsButton>
        <LdsButton
          classes="primary"
          onClick={handleProceedToServiceNow}
        >
          Open GSS Incident Form ↗
        </LdsButton>
      </div>
    </LdsModal>
  );
};

export default ServiceNowGuidanceModal;
